import { offset, shift, useFloating, FloatingArrow } from "@floating-ui/react";
import { captureException } from "@sentry/nextjs";
import clsx from "clsx";
import { FocusTrap } from "focus-trap-react";
import debounce from "lodash/debounce";
import { useSearchParams } from "next/navigation";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { CSSTransition } from "react-transition-group";

import * as shareButtonsStyles from "@ui/components/article/ShareButtons/ShareButtons.css";
import Button from "@ui/components/cta/Button";
import CloseIcon from "@ui/components/icon/CloseIcon";
import GiftIcon from "@ui/components/icon/GiftIcon";
import LinkIcon from "@ui/components/icon/LinkIcon";
import { ArticleLayout } from "@ui/components/ledes/ledes.types";
import DuetLink from "@ui/components/navigation/DuetLink";
import { BREAKPOINTS } from "@ui/config";
import { useComponentConfig } from "@ui/context/component-config-context";
import { sharedStyles } from "@ui/shared-styles";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useConfigContext } from "@voxmedia/duet-context/context/config-context";
import { usePaywall } from "@voxmedia/duet-context/context/paywall-context";
import { useIsSmallerThanBreakpoint } from "@voxmedia/duet-utils/use-is-smaller-than-breakpoint";
import { useScrollLock } from "@voxmedia/duet-utils/use-scroll-lock";

import * as styles from "./ShareButtonsGiftLink.css";

interface Props {
  url: string;
  layout: ArticleLayout | "stream" | "video";
}

const ShareButtonsGiftLink: FunctionComponent<Props> = ({ url, layout }) => {
  const { isPaywallEligible } = usePaywall();
  const searchParams = useSearchParams();
  const analytics = useAnalytics();
  const auth = useAuth();
  const {
    networkConfig,
    membershipConfig: { giftArticleMonthlyLimit = 0 },
  } = useConfigContext();

  const { refs, floatingStyles, update, context } = useFloating({
    middleware: [offset(10), shift()],
  });

  const shareButtonsConfig = useComponentConfig("shareButtons");
  const { giftLinks, popoverClassname } = shareButtonsConfig;

  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [giftCount, setGiftCount] = useState<number | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const isEntitled = auth.user?.isEntitled || false;
  const isSignedIn = !!auth.user;

  // Set user's share limit
  useEffect(() => {
    const fetchUserMetadata = async () => {
      try {
        const response = await fetch("/api/account/gifted-articles-count");
        const { count } = (await response.json()) as {
          count: number;
        };
        const giftArticlesAvailable = giftArticleMonthlyLimit - (count || 0);

        setGiftCount(Math.max(giftArticlesAvailable, 0));
      } catch (error) {
        captureException(error, {
          extra: {
            message: "Error fetching user metadata:",
          },
        });
      }
    };

    void fetchUserMetadata();
  }, [giftArticleMonthlyLimit, networkConfig]);

  const popoverChange = useCallback(
    (open: boolean) => {
      if (open) {
        update();
      }
      setPopoverOpen(open);
    },
    [update],
  );

  const isMobile = useIsSmallerThanBreakpoint(BREAKPOINTS.tablet);
  const opacityLayerRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef(null);

  // Close the popover when clicking outside
  useEffect(() => {
    function close(e: Event) {
      if (
        refs.reference.current &&
        !refs.floating.current?.contains(e.target as Node) &&
        !(refs.reference.current as HTMLElement).contains(e.target as Node)
      ) {
        popoverChange(false);
      }
    }

    // Close the popover when scrolling significantly
    let lastScrollY = window.scrollY;
    const handleScroll = debounce(() => {
      const scrolled = Math.abs(window.scrollY - lastScrollY);
      if (scrolled > 50) {
        close(new Event("scroll"));
      }
      lastScrollY = window.scrollY;
    }, 100);

    if (popoverOpen) {
      document.addEventListener("click", close);
      document.addEventListener("scroll", handleScroll);
    }

    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", handleScroll);
    };
  }, [refs.floating, refs.reference, popoverChange, popoverOpen]);

  useEffect(() => {
    // need to run this in a hook because it uses window.location,
    // which is not available in SSR. We attach an additional query
    // param to the URL so that the popup opens on load
    const url = new URL(window.location.href);
    url.searchParams.set("giftLink", "true");
    const returnPath = encodeURIComponent(url.pathname + url.search);
    setRedirectUrl(returnPath);
  }, []);

  // Open popup if giftLink param is set on URL
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (searchParams && searchParams.has("giftLink")) {
      setPopoverOpen(true);
    }
  }, [searchParams]);

  // Lock scroll on mobile when popover is open
  useScrollLock(popoverOpen && isMobile);

  // If the vm-resource-is-paywall-eligible header is set, hide this component
  if (!isPaywallEligible) {
    return null;
  }

  const handleCopyLink = () => {
    const { pathname } = new URL(url);
    setCopyFeedback("Getting gift link...");
    navigator.clipboard
      .write([
        new ClipboardItem({
          "text/plain": fetch("/api/account/generate-article-gift-link", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              origin: window.location.origin,
            },
            body: JSON.stringify({
              path: pathname,
            }),
          })
            .then((response) => response.json())
            .then(({ data }) => {
              const { giftUrl, count } = data as {
                giftUrl: string | null;
                count: number;
              };

              if (!giftUrl) {
                setCopyFeedback("Failed to copy gift link");
                return "";
              } else {
                setGiftCount(giftArticleMonthlyLimit - count);
                setCopyFeedback("Gift link copied!");
                return giftUrl;
              }
            })
            .catch((err) => {
              setCopyFeedback("Failed to copy gift link");
              captureException(err, {
                extra: {
                  message: "There was an error generating the gift link",
                },
              });
              return "";
            }),
        }),
      ])
      .catch((err) => {
        console.error(err);
        setCopyFeedback("Failed to copy");
      });

    analytics?.emit({
      event: "gift_link_share",
      gift_link_share: { article_url: url },
    });
  };

  return (
    <>
      <div ref={refs.setReference}>
        <button
          onClick={() => popoverChange(!popoverOpen)}
          aria-expanded={popoverOpen}
          aria-haspopup="true"
          aria-label="Gift this article"
          className={shareButtonsStyles.button[layout]}
        >
          <GiftIcon
            className={clsx(
              shareButtonsStyles.icon[layout],
              styles.shareIcon[layout],
            )}
          />
        </button>
      </div>

      {/* Mobile opacity layer */}
      <CSSTransition
        in={popoverOpen && isMobile}
        timeout={200}
        nodeRef={opacityLayerRef}
        classNames={{
          enter: shareButtonsStyles.opacityLayerEnter,
          enterActive: shareButtonsStyles.opacityLayerEnterActive,
          exit: shareButtonsStyles.opacityLayerExit,
          exitActive: shareButtonsStyles.opacityLayerExitActive,
        }}
        unmountOnExit
      >
        <div
          ref={opacityLayerRef}
          className={shareButtonsStyles.opacityLayer}
        />
      </CSSTransition>

      {/* Share popover */}
      <CSSTransition
        in={popoverOpen}
        timeout={200}
        nodeRef={refs.floating}
        classNames={{
          enter: shareButtonsStyles.sharePopoverEnter,
          enterActive: shareButtonsStyles.sharePopoverEnterActive,
          exit: shareButtonsStyles.sharePopoverExit,
          exitActive: shareButtonsStyles.sharePopoverExitActive,
        }}
        unmountOnExit
      >
        <FocusTrap
          active={popoverOpen}
          focusTrapOptions={{
            fallbackFocus: `#share-popover-${Date.now()}`,
            escapeDeactivates: true,
            clickOutsideDeactivates: false,
            allowOutsideClick: true,
          }}
        >
          <div
            id={`share-popover-${Date.now()}`}
            style={{
              ...(isMobile ? {} : floatingStyles),
            }}
            ref={refs.setFloating}
            className={clsx(shareButtonsStyles.sharePopover, popoverClassname)}
            role="dialog"
            aria-modal="true"
            aria-label="Share options"
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={shareButtonsStyles.sharePopoverArrow}
            />
            <div className={shareButtonsStyles.sharePopoverContent}>
              <div className={shareButtonsStyles.shareHeadingContainer}>
                <div className={shareButtonsStyles.shareHeading}>
                  Gift this article
                </div>
                <button onClick={() => popoverChange(false)}>
                  <CloseIcon
                    className={shareButtonsStyles.dismissIcon}
                    width={16}
                    height={16}
                  />
                </button>
              </div>
              {isEntitled ? (
                <>
                  <p className={styles.description}>
                    {giftLinks.subscriberMessaging(giftCount)}
                  </p>
                  <Button
                    onClick={handleCopyLink}
                    className={clsx(
                      (copyFeedback || giftCount === 0) &&
                        styles.buttonDisabled,
                    )}
                    displayType="primary"
                    fullWidth
                    iconLeft={<LinkIcon className={styles.icon} />}
                    text={copyFeedback || "Copy gift link"}
                    disabled={!!copyFeedback || giftCount === 0}
                  />
                </>
              ) : (
                <>
                  <p className={styles.description}>
                    {giftLinks.nonSubscriberMessaging}
                  </p>
                  <Button
                    href={`${giftLinks.ctaLink || ""}${redirectUrl ? `?returnUrl=${redirectUrl}` : ""}`}
                    displayType="primary"
                    fullWidth
                    text={giftLinks.ctaText || "Become a Member"}
                    className={styles.becomeAMemberButton}
                    onClick={() => {
                      analytics?.emit({
                        event: "gift_link_share_become_member",
                        gift_link_share_become_member: { article_url: url },
                      });
                    }}
                  />
                  {!isSignedIn && (
                    <div className={styles.signInCTA}>
                      {giftLinks.signInText || "Already a member?"}{" "}
                      <DuetLink
                        href={`/auth/login${redirectUrl ? `?returnPath=${redirectUrl}` : ""}`}
                        className={sharedStyles.text.singleLink}
                        onClick={() => {
                          analytics?.emit({
                            event: "authentication",
                            authentication: {
                              article_url: url,
                              method: "gift_link_login",
                            },
                          });
                        }}
                      >
                        Sign In
                      </DuetLink>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </FocusTrap>
      </CSSTransition>
    </>
  );
};

export default ShareButtonsGiftLink;
